import { Textbit, type Element } from '@ttab/textbit'
import { LoadingText } from './LoadingText'
import { useDocumentSnapshot } from '@/hooks'
import { Bold, Italic, Link, Text, OrderedList, UnorderedList, TTVisual, Image, Factbox, Table } from '@ttab/textbit-plugins'
import { PreVersion } from './Version/PreVersion'
import type { Status as DocumentStatuses } from '@ttab/elephant-api/repository'
import { PreVersionInfo } from './Version/PreVersionInfo'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ttab/elephant-ui/utils'
import { AlertDescription } from '@ttab/elephant-ui'
import { MessageCircleMoreIcon } from '@ttab/elephant-ui/icons'

export const Editor = ({ id, version, textOnly = false, direct, versionStatusHistory, disableScroll = false, showNotes = false, showTitle = false }: {
  id: string
  textOnly?: boolean
  version?: bigint | undefined
  versionStatusHistory?: DocumentStatuses[]
  direct?: boolean
  disableScroll?: boolean
  showNotes?: boolean
  showTitle?: boolean
}): JSX.Element => {
  const { t, i18n } = useTranslation('editor')

  const activeLocale = i18n.resolvedLanguage

  const getPlugins = () => {
    const basePlugins = [Text, UnorderedList, OrderedList, Bold, Italic, Link, Table]
    return [
      ...basePlugins.map((initPlugin) => initPlugin()),
      TTVisual({
        captionLabel: t('image.captionLabel'),
        bylineLabel: t('image.bylineLabel'),
        removable: false
      }),
      Image({
        captionLabel: t('image.captionLabel'),
        bylineLabel: t('image.bylineLabel'),
        removable: false
      }),
      Factbox({
        headerTitle: t('factbox.headerTitle'),
        modifiedLabel: t('factbox.modifiedLabel'),
        createdLabel: t('factbox.createdLabel'),
        lastModifiedLabel: t('factbox.lastModifiedLabel'),
        footerTitle: t('factbox.footerTitle'),
        removable: false,
        locale: activeLocale
      })
    ]
  }

  const { data: document, error } = useDocumentSnapshot({ id, version, direct })

  if (error) {
    return <div>{t('errors:messages.loadFailed')}</div>
  }

  if (!document) {
    return (
      <LoadingText>
        {t('common:misc.loading')}
      </LoadingText>
    )
  }

  const content = document.content

  if (!content.length && document.meta && document.links) {
    // Preversion-preview: render non-article types of documents, such as Planning or Event
    return (
      <PreVersion
        content={document}
        version={version}
        versionStatusHistory={versionStatusHistory}
      />
    )
  }

  const notes = showNotes ? (document.meta['core/note'] ?? []) : []

  return (
    <div className={cn(
      'flex flex-col w-full pb-6 overflow-x-hidden max-w-(--breakpoint-lg) mx-auto',
      !disableScroll && 'overflow-y-auto'
    )}
    >
      {versionStatusHistory && version && (
        <PreVersionInfo version={version} versionStatusHistory={versionStatusHistory} />
      )}

      {showTitle && document.title && (
        <p className='text-lg font-bold pt-2 ps-12 pe-12 '>
          {document.title}
        </p>
      )}

      {!!notes.length && (
        <div className='pe-12 ps-12 mt-6'>
          {notes.map((note, index) => (
            <div key={index} className='flex flex-row gap-2 w-full justify-between items-center'>
              <div className='flex gap-3 py-3 px-4  bg-blue-50 dark:bg-blue-800 items-center w-full border rounded border-blue-500/30 dark:border-blue-300/50'>
                <MessageCircleMoreIcon size={18} strokeWidth={1.75} className='shrink-0 stroke-blue-800/50 dark:stroke-blue-300 fill-[#ffffff] dark:fill-gray-800' />
                <AlertDescription>{note.data?.text}</AlertDescription>
              </div>
            </div>
          ))}
        </div>
      )}

      <Textbit.Root
        key={`${id}-${version ?? 'latest'}`}
        value={filterText(content, textOnly)}
        plugins={getPlugins()}
        readOnly
      >
        <Textbit.Editable
          className={`outline-none
            pt-4
            pb-4
            ps-12
            pe-12
            dark:text-slate-100
            **:data-spelling-error:border-b-2
            **:data-spelling-error:border-dotted
            **:data-spelling-error:border-red-500
            grow
            pr-12
            max-w-(--breakpoint-xl)`}
        />
      </Textbit.Root>
    </div>

  )
}

function filterText(content: Element[], textOnly: boolean): Element[] {
  if (!textOnly) {
    return content
  }

  return content.filter((c) => c.type !== 'tt/visual' && c.type !== 'core/image')
}
